import {Intervals} from "../../Intervals"

/**
 * System Health Report settings schema.
 *
 * Unlike Detailed Alarm / Alarm Resolution, this report HAS manual-run settings:
 * three toggles that choose which check sections appear. They are not template-
 * only, so they render in the Report Settings drawer on a manual run (and the
 * drawer's trigger button shows). The delivery schedule stays template-only.
 * Setting keys (`show_health_checks`, etc.) are unchanged from the pre-schema
 * SystemHealthReportSettings.vue, so saved templates are unaffected.
 */
export default {
    slug: 'system-health-report',
    icon:
        '<path stroke="none" d="M0 0h24v24H0z" fill="none"/>'
        + '<path d="M19.5 13.572l-7.5 7.428l-2.896 -2.868m-6.117 -8.104a5 5 0 0 1 9.013 -3.022a5 5 0 1 1 7.5 6.572" />'
        + '<path d="M3 13h2l2 3l2 -6l1 3h3" />',
    permission: 'manage_system_health_report',
    title: 'System Health',
    description: 'Which devices are online and reporting, and which ones are not.',
    info: 'A snapshot of your site\'s monitoring setup at the moment the report runs. Depending on the sections you turn on, it covers alarm and notification setup checks, probe calibration certificate status, and device battery levels.',

    sections: [
        {
            title: 'Data Settings',
            description: 'Choose which health checks appear in the generated report.',
            fields: [
                {
                    key: 'show_health_checks',
                    type: 'toggle',
                    label: 'Health Checks',
                    description: 'Adds checks of your alarm setup, each marked Passed, Needs Review or Issues Identified: active and snoozed alarms, points needing activation, zones without alarm recipients, users with notifications off, and SNAP probe status.',
                },
                {
                    key: 'show_battery_checks',
                    type: 'toggle',
                    label: 'Battery Checks',
                    description: 'Lists each device with its latest battery voltage and how long until it needs charging, and flags devices where Battery Saver Mode is recommended.',
                },
                {
                    key: 'show_calibration_checks',
                    type: 'toggle',
                    label: 'Calibration Checks',
                    description: 'Shows SNAP enrollment and each probe\'s NIST certificate dates, grouped into current, expired in the last year, and expired over a year ago. Certificates expiring within about 2 months are flagged.',
                },
            ],
        },

        {
            // Template-only: the recurring send schedule. On-demand runs snapshot
            // the current state, so there is no window to choose here.
            title: 'Scheduled Delivery',
            description: 'Automatically send this report on a recurring schedule.',
            showIf: ({isSavable}) => isSavable === true,
            fields: [
                {
                    key: 'interval',
                    type: 'select',
                    label: 'Send frequency',
                    description: 'How often the report is emailed, around 1 AM site time. Monthly sends on the 1st; semi-monthly on the 1st and 16th. Each send is a fresh snapshot.',
                    options: Intervals.STANDARD_INTERVAL_OPTIONS,
                    fromContext: true,
                },
                {
                    key: 'weekday',
                    type: 'select',
                    label: 'Day to receive report',
                    description: 'The day the weekly report is emailed, around 1 AM site time.',
                    options: Intervals.WEEKDAY_OPTIONS,
                    showIf: ({interval}) => interval === Intervals.WEEKLY,
                },
            ],
        },
    ],
}
